import { StrictOmit } from "@instacover/utils"
import { UpdateSpec } from "dexie"

import { LINKS_NAVIGATION } from "@/constants/orderedSteps"
import { Tables } from "@/database/helpers/tables"
import { Repository } from "@/database/helpers/types"
import { OrderedStepLocal } from "@/types/stepTypes"
import { logger } from "@/utils/logging"

type UpdateParams = {
  name: OrderedStepLocal["name"]
  instahomeSessionId: string
  data: StrictOmit<UpdateSpec<OrderedStepLocal>, "dexieId">
}

type UpsertData = Parameters<Tables["orderedStep"]["add"]>[0]

type FinishAndGetNextParams = {
  sessionId: string
  step: OrderedStepLocal["name"]
}

export class OrderedStepRepository extends Repository {
  async upsert(data: UpsertData) {
    const record = await this.dexie.orderedStep.where("name").equals(data.name).first()
    if (record) {
      await this.dexie.orderedStep.update(record.dexieId, { ...data })
      return
    }

    await this.dexie.orderedStep.add(data)
  }

  async findAll(instahomeSessionId: string) {
    return await this.dexie.orderedStep.where("instahomeSessionId").anyOf(instahomeSessionId).toArray()
  }

  async get(name: string, instahomeSessionId: string) {
    return await this.dexie.orderedStep
      .where("instahomeSessionId")
      .equals(instahomeSessionId)
      .and((s) => s.name === name)
      .first()
  }

  async update(params: UpdateParams) {
    const { data, instahomeSessionId, name } = params
    logger.debug(`Updating ordered step ${name} for session ${instahomeSessionId} with data:`, data)

    const step = await this.get(name, instahomeSessionId)
    if (!step) {
      logger.error(`Ordered step ${name} for session ${instahomeSessionId} not found`)
      return
    }

    await this.dexie.orderedStep.update(step.dexieId, { ...data })
  }

  async deleteAllBySessionId(instahomeSessionId: string) {
    const records = await this.dexie.orderedStep.where("instahomeSessionId").equals(instahomeSessionId).toArray()

    for (const record of records) {
      await this.dexie.orderedStep.delete(record.dexieId)
    }
  }

  async finishAndGetNext(params: FinishAndGetNextParams) {
    const { sessionId, step } = params

    const result = await this.dexie.transaction("rw", this.dexie.orderedStep, async () => {
      await this.update({ name: step, instahomeSessionId: sessionId, data: { isFinished: true } })

      const unfinishedSteps = await this.dexie.orderedStep
        .where("instahomeSessionId")
        .equals(sessionId)
        .and((step) => step.isFinished === false)
        .toArray()

      const firstUnfinishedStep = unfinishedSteps[0]
      if (!firstUnfinishedStep) return

      const firstUnfinishedStepLinks = LINKS_NAVIGATION[firstUnfinishedStep.name]
      return { to: firstUnfinishedStepLinks, params: { sessionId } }
    })

    return result
  }
}
